/**
 * «¿Tenés una página web?» — el paso que ofrece y no exige (GKTIBO-65).
 *
 * Dos cosas que este componente existe para no hacer:
 *
 * 1. **Parecer obligatorio.** Un negocio sin web tiene que poder terminar el
 *    alta (regla C5b, ADR-023). Declinar avanza igual, sin marca de error: la
 *    diferencia entre un paso y un peaje, y un peaje en el alta es la razón
 *    número uno por la que alguien la abandona.
 * 2. **Quedarse mudo.** El rastreo tarda treinta segundos o más. Mientras
 *    corre dice cuántas páginas leyó, no un spinner: un spinner largo se lee
 *    como «se colgó».
 */

import type { PaginaLeidaResumen } from '@chatbot/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api.ts';
import { PaginasDelSitio } from './PaginasDelSitio';
import {
  type Descartada,
  esPedible,
  explicar,
  loQueSigueAfuera,
  porQueNoArranco,
  porQueNoSePudoPedir,
  sinRepetir,
} from './motivos-web';
import { textos } from './textos';
import { paginas as textosDePaginas } from './textos-web';

const CADA_CUANTO_MS = 2_000;
/**
 * A partir de acá el rastreo dejó de ser «está tardando» y pasó a ser «algo
 * pasó»: el worker tiene cinco minutos, más que eso es que murió o que el
 * mensaje se fue a la DLQ. Seguir preguntando cada dos segundos para siempre
 * es una pantalla colgada sin explicación, y un endpoint golpeado por cada
 * pestaña abierta.
 */
const HASTA_CUANDO_MS = 6 * 60_000;

export function SeccionDeLaPagina({ tenantId }: { tenantId: string }) {
  const [url, setUrl] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [seColgo, setSeColgo] = useState(false);
  const [declinada, setDeclinada] = useState(false);
  const [errorAlEncolar, setErrorAlEncolar] = useState<string | null>(null);
  /**
   * Lo leído por los trabajos anteriores de ESTA dirección. Un «Leer estas
   * también» es otro trabajo, que sólo trae lo pedido: mostrar sólo eso le
   * diría al dueño que la home ya no se leyó (GKTIBO-136).
   */
  const [anteriores, setAnteriores] = useState<PaginaLeidaResumen[]>([]);
  /** Lo que quedó afuera en trabajos anteriores y todavía no se pidió (I2). */
  const [afuera, setAfuera] = useState<Descartada[]>([]);
  /** El trabajo actual es un «Leer estas también». Cambia qué se dice si falla. */
  const [esUnPedido, setEsUnPedido] = useState(false);
  /** Entre el clic y el jobId nuevo: el botón no se puede apretar dos veces. */
  const [pidiendo, setPidiendo] = useState(false);

  const { data: avance } = useQuery({
    queryKey: ['ingesta-web', tenantId, jobId],
    queryFn: () => api.tenants.avanceDeIngesta(tenantId, jobId as string),
    enabled: Boolean(jobId),
    // Deja de preguntar cuando el trabajo terminó, de una forma u otra.
    // Un 404 al principio es la carrera normal —el panel pregunta antes de que
    // el worker escriba la primera fila— y se recupera solo. Por eso reintenta.
    retry: true,
    refetchInterval: (q) => {
      if (q.state.data && q.state.data.estado !== 'rastreando') return false;
      if (seColgo) return false;
      return CADA_CUANTO_MS;
    },
  });

  // El aviso lo dispara un reloj propio, no el render: al cortar el polling
  // dejan de llegar renders, y un cálculo «al dibujar» nunca volvería a correr.
  useEffect(() => {
    if (!jobId) return;
    const reloj = setTimeout(() => setSeColgo(true), HASTA_CUANDO_MS);
    return () => clearTimeout(reloj);
  }, [jobId]);

  const colgado = seColgo && (!avance || avance.estado === 'rastreando');
  const corriendo = Boolean(jobId) && !colgado && (!avance || avance.estado === 'rastreando');
  const fallo = avance?.estado === 'fallido';
  // Respondió y no trajo nada: no es un éxito, aunque el worker lo llame así.
  const vacio = avance?.estado === 'terminado' && avance.paginasLeidas === 0;

  async function leer(urls?: string[]) {
    setErrorAlEncolar(null);
    try {
      // Sin pedido, la llamada de siempre: dos argumentos, no un tercero vacío.
      const { jobId: id } = urls
        ? await api.tenants.leerMiPagina(tenantId, url.trim(), urls)
        : await api.tenants.leerMiPagina(tenantId, url.trim());
      setSeColgo(false);
      setEsUnPedido(Boolean(urls));
      setJobId(id);
    } catch (err) {
      setErrorAlEncolar(urls ? porQueNoSePudoPedir(err) : porQueNoArranco(err));
    }
  }

  /**
   * «Leer estas también»: otro trabajo. Lo ya leído y lo que sigue afuera se
   * conservan a la vista: el trabajo nuevo sólo trae lo pedido.
   */
  async function pedirMas(urls: string[]) {
    setAnteriores((antes) => sinRepetir([...antes, ...(avance?.leidas ?? [])]));
    // Las pedidas NO se sacan acá. Hasta que el trabajo termine no se sabe
    // si se leyeron, y sacarlas de una hacía desaparecer las que fallaban:
    // el aviso decía «dos no se pudieron leer» y no quedaba forma de volver
    // a pedirlas (ronda 3, I4). `loQueSigueAfuera` saca las que sí se leyeron.
    setAfuera((antes) => sinRepetir([...antes, ...(avance?.descartadas ?? [])]));
    setPidiendo(true);
    try {
      await leer(urls);
    } finally {
      setPidiendo(false);
    }
  }

  /** Volver al formulario. Sin esto, un rastreo fallido es un callejón. */
  function empezarDeNuevo() {
    setJobId(null);
    setSeColgo(false);
    setErrorAlEncolar(null);
    setAnteriores([]);
    setAfuera([]);
    setEsUnPedido(false);
  }

  // Las listas juntan todos los trabajos de esta dirección. Un pedido que
  // falló no las borra: lo leído antes sigue leído (m5).
  const leidas = sinRepetir([...anteriores, ...(avance?.leidas ?? [])]);
  const descartadas = loQueSigueAfuera([...afuera, ...(avance?.descartadas ?? [])], leidas);
  /**
   * «Hay más» es una afirmación sobre el SITIO, no una promesa de botón: el
   * copy dice «hay más, pero con esto alcanza para empezar». Por eso sigue
   * atado a `truncado` aunque no haya nada pedible.
   *
   * Lo que sí era un callejón, y se arregló en el rastreador, es que el tope
   * de profundidad ponía `truncado` sin anotar las páginas que dejaba atrás:
   * no se podían pedir porque no estaban listadas (ronda 3, I3).
   */
  const hayMas = Boolean(avance?.truncado) || descartadas.some(esPedible);
  // Un pedido que falló, o que terminó sin leer nada, no borra lo anterior (m5, m9).
  const pedidoFallido = esUnPedido && (fallo || vacio) && leidas.length > 0;
  const terminado = (avance?.estado === 'terminado' && !vacio) || pedidoFallido;
  // Pidió tres y se leyeron dos: se dice (m8).
  const noLeidas = esUnPedido && !pedidoFallido ? (avance?.fallas.length ?? 0) : 0;

  if (declinada) {
    // Sin web: se dice en positivo y se sigue. Nada de rojo, nada de advertencia.
    return (
      <div className="alta-web" data-testid="alta-web">
        <p className="alta-web-sin">{textos.conocimiento.web.sinWeb}</p>
      </div>
    );
  }

  return (
    <div className="alta-web" data-testid="alta-web">
      <h3 className="alta-web-titulo">{textos.conocimiento.web.titulo}</h3>
      <p className="alta-web-ayuda">{textos.conocimiento.web.ayuda}</p>

      {(!jobId || (fallo && !pedidoFallido) || colgado || (vacio && !pedidoFallido)) && (
        <div className="alta-web-form">
          <label className="alta-web-etiqueta" htmlFor="alta-web-url">
            {textos.conocimiento.web.etiqueta}
          </label>
          <input
            id="alta-web-url"
            className="alta-web-input"
            type="url"
            inputMode="url"
            placeholder={textos.conocimiento.web.ejemplo}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          {/* Los dos por `Button`, no por clases a mano. `.btn` sólo trae forma
              y tipografía: el ALTO y el padding los pone la clase de tamaño
              (`btn-md`), que al escribir `btn btn-primary` suelto nadie pone.
              Por eso este botón se veía más chico que todos los demás del
              alta. El componente compone variante y tamaño y no deja olvidar
              ninguno. */}
          <div className="alta-web-acciones">
            <Button
              disabled={url.trim().length === 0}
              onClick={() => {
                if (jobId) empezarDeNuevo();
                void leer();
              }}
            >
              {jobId ? textos.conocimiento.web.otraDireccion : textos.conocimiento.web.leer}
            </Button>
            {/* Declinar es un camino de primera, no una letra chica. */}
            <Button variant="ghost" onClick={() => setDeclinada(true)}>
              {textos.conocimiento.web.noTengo}
            </Button>
          </div>
        </div>
      )}

      {/* Fuera del formulario: un pedido rechazado con un resultado ya en
          pantalla se lo tragaba, porque el formulario no está (I1). */}
      {errorAlEncolar && (
        <p className="alta-web-aviso" data-testid="alta-web-aviso">
          {errorAlEncolar}
        </p>
      )}

      {corriendo && (
        /* El texto dice QUÉ está pasando y la barra dice que SIGUE pasando.
           Hacen falta los dos: el contador de páginas puede quedarse quieto
           medio minuto en un sitio lento, y un número que no se mueve se lee
           igual que una pantalla colgada. La barra no simula progreso —no
           sabemos cuántas páginas hay— y por eso es indeterminada. */
        <div className="alta-web-avance" data-testid="alta-web-avance">
          {/* `status` y no `progressbar`: una barra indeterminada no tiene
              valor, mínimo ni máximo que anunciar. Quien no la ve se lleva el
              texto, que dice lo mismo y además cuántas páginas van; la barra
              es la versión visual de eso y por eso está oculta al lector. */}
          <output className="alta-web-avance-texto">
            {esUnPedido
              ? textosDePaginas.leyendo
              : textos.conocimiento.web.leyendo(avance?.paginasLeidas ?? 0)}
          </output>
          <div className="alta-web-barra" aria-hidden>
            <span className="alta-web-barra-pulso" />
          </div>
        </div>
      )}

      {colgado && (
        <p className="alta-web-aviso" data-testid="alta-web-aviso">
          {textos.conocimiento.web.tardaDemasiado}
        </p>
      )}

      {terminado && avance && (
        <>
          {pedidoFallido ? (
            <p className="alta-web-aviso" data-testid="alta-web-aviso">
              {textosDePaginas.noSePudoLeerLoPedido}
            </p>
          ) : (
            <p className="alta-web-listo" data-testid="alta-web-listo">
              {hayMas
                ? textos.conocimiento.web.listoHayMas(leidas.length)
                : textos.conocimiento.web.listo(leidas.length)}{' '}
              {textos.conocimiento.web.revisar}
            </p>
          )}
          {/* Su propio testid: con un error de encolado a la vista habría
              dos nodos con el mismo y `getByTestId` lanza (ronda 3, M4). */}
          {noLeidas > 0 && (
            <p className="alta-web-aviso" data-testid="alta-web-no-leidas">
              {textosDePaginas.noSeLeyeron(noLeidas)}
            </p>
          )}
          {/* Qué se leyó y qué no, y pedir más (GKTIBO-136). `key` por trabajo:
              la elección de casillas es de ESTE resultado, no del anterior. */}
          <PaginasDelSitio
            key={jobId}
            leidas={leidas}
            descartadas={descartadas}
            truncado={hayMas}
            onPedir={(urls) => void pedirMas(urls)}
            pidiendo={pidiendo}
          />
        </>
      )}

      {vacio && !pedidoFallido && (
        <p className="alta-web-aviso" data-testid="alta-web-aviso">
          {textos.conocimiento.web.sinNada}
        </p>
      )}

      {fallo && !pedidoFallido && (
        // No es un error del dueño y no se muestra como tal: se explica por qué
        // y se lo empuja a seguir, que es lo que de verdad necesita. El `motivo`
        // de arriba es el del bug del rastreador, que no deja `fallas`.
        <p className="alta-web-aviso" data-testid="alta-web-aviso">
          {explicar(avance.fallas[0]?.motivo ?? avance.motivo)}
        </p>
      )}
    </div>
  );
}
